package gr.tech.GRAttestation.sections.tables

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.browser.dom.ElementTarget
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.icons.fa.FaEllipsisVertical
import com.varabyte.kobweb.silk.components.overlay.PopupPlacement
import com.varabyte.kobweb.silk.components.overlay.Tooltip
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.GRAttestation.models.stores.STORE_STATUS
import gr.tech.GRAttestation.models.stores.StoreDTO
import gr.tech.GRAttestation.sections.admin.AdminTableContainer
import gr.tech.GRAttestation.styles.admin.AdminTableStickyColumn
import gr.tech.GRAttestation.styles.grstyles.DropdownLinkStyle
import gr.tech.GRAttestation.util.TimeUtils.formatDateOnly
import gr.tech.GRAttestation.util.TimeUtils.formatDateTime
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*

@Composable
fun AdminStoresTable(
    stores: List<StoreDTO>,
    onView: (StoreDTO) -> Unit,
    onEdit: (StoreDTO) -> Unit,
    onViewPDFs: (StoreDTO) -> Unit,
    onDelete: (StoreDTO) -> Unit,
    onRestore: (StoreDTO) -> Unit,
) {
    AdminTableContainer() {
        Table(
            attrs = Modifier
                .classNames("table")
                .minWidth(1000.px)
                .height(100.percent)
                .fillMaxWidth()
                .toAttrs()
        ) {
            Thead(
                attrs = Modifier
                    .styleModifier {
                        property("table-layout", "fixed")
                    }
                    .toAttrs()
            ) {
                Tr {
                    Th(
                        attrs = Modifier.minWidth(75.px).classNames("col").toAttrs()
                    ) { Text("ID") }
                    Th(
                        attrs = Modifier.minWidth(105.px).classNames("col").toAttrs()
                    ) { Text("Location ID") }
                    listOf(
                        "Store Name",
                        "Store Rep",
                        "Address",
                    ).forEach { header ->
                        Th(
                            attrs = Modifier.minWidth(175.px).classNames("col").toAttrs()
                        ) { Text(header) }
                    }
                    listOf(
                        "City",
                        "State",
                        "ZIP",
                        "Created",
                        "Updated",
                    ).forEach { header ->
                        Th(
                            attrs = Modifier.minWidth(100.px).classNames("col").toAttrs()
                        ) { Text(header) }
                    }
                    Th(
                        attrs = Modifier
                            .then(AdminTableStickyColumn.toModifier())
                            .width(150.px)
                            .toAttrs()
                    ) {
                        Text("Action")
                    }
                }
            }
            Tbody {
                if (stores.isNotEmpty()) {
                    stores.forEach { project ->
                        AdminStoreTableRow(
                            store = project,
                            onView = { onView(it) },
                            onEdit = { onEdit(it) },
                            onViewPDFs = { onViewPDFs(it) },
                            onDelete = { onDelete(it) },
                            onRestore = { onRestore(it) }
                        )
                    }
                } else {
                    repeat(2) {
                        AdminActivitySummaryRow(null)
                    }
                }
            }
        }
    }
}


@Composable
fun AdminStoreTableRow(
    store: StoreDTO,
    onView: (StoreDTO) -> Unit,
    onEdit: (StoreDTO) -> Unit,
    onViewPDFs: (StoreDTO) -> Unit,
    onDelete: (StoreDTO) -> Unit,
    onRestore: (StoreDTO) -> Unit,
) {
    Tr {
        Td { Text(store.id.toString()) }
        Td { Text(store.locationId) }
        Td { Text(store.storeName) }
        Td { Text(store.storeRep) }
        Td { Text(store.storeAddress) }
        Td { Text(store.storeCity) }
        Td { Text(store.storeState) }
        Td { Text(store.storeZip) }
        Td {
            Span { Text(formatDateOnly(store.createdAt)) }
            TableDateTimeTooltip(store.createdAt)
        }
        Td {
            Span { Text(formatDateOnly(store.updatedAt)) }
            TableDateTimeTooltip(store.updatedAt)
        }

        // Action column
        Td(
            attrs = Modifier
                .padding(.5.cssRem)
                .then(AdminTableStickyColumn.toModifier())
                .toAttrs()
        ) {
            Div(
                attrs = Modifier
                    .classNames("btn-group")
                    .toAttrs()
            ) {
                Div(
                    attrs = Modifier
                        .classNames("btn-group", "dropstart")
                        .toAttrs()
                ) {
                    Button(
                        attrs = Modifier
                            .classNames("btn", "btn-outline-secondary")
                            .toAttrs{
                                attr("data-bs-toggle", "dropdown")
                            }
                    ) {
                        FaEllipsisVertical()
                    }
                    Ul(
                        attrs = Modifier.classNames("dropdown-menu")
                            .toAttrs()
                    ) {
                        Li {
                            Span(
                                attrs = DropdownLinkStyle.toModifier().classNames("dropdown-item")
                                    .onClick { onEdit(store) }
                                    .toAttrs()
                            ) {
                                Text("Edit")
                            }
                        }
                        Li {
                            Span(
                                attrs = DropdownLinkStyle.toModifier().classNames("dropdown-item")
                                    .onClick {
                                        if(store.status == STORE_STATUS.DELETED.bit) {
                                            onRestore(store)
                                        } else {
                                            onDelete(store)
                                        }
                                    }
                                    .toAttrs()
                            ) {
                                if(store.status == STORE_STATUS.DELETED.bit) {
                                    Text("Restore")
                                } else {
                                    Text("Delete")
                                }
                            }
                        }
                    }
                }
                Button(
                    attrs = Modifier.classNames("btn", "btn-outline-secondary")
                        .onClick { onView(store) }
                        .toAttrs(),
                ) {
                    Text("View")
                }
            }
        }
    }
}

@Composable
fun TableDateTimeTooltip(isoDateTime: String) {
    Tooltip(
        target = ElementTarget.PreviousSibling,
        modifier = Modifier.padding(4.px),
        placement = PopupPlacement.Top,
        hasArrow = true,
        showDelayMs = 50,
        hideDelayMs = 50,
    ) { Text(formatDateTime(isoDateTime)) }
}